import React from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import EventIcon from '@material-ui/icons/Event';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import PersonIcon from '@material-ui/icons/Person';
import EventSeatIcon from '@material-ui/icons/EventSeat';
import { planLabel, visibleSeatFields } from 'utils/learningHubUi';

const useStyles = makeStyles((theme) => ({
  accessStrip: {
    marginTop: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    padding: theme.spacing(0.75),
    borderRadius: 16,
    border: '1px solid var(--dm-border, #e7ebf0)',
    backgroundColor: 'var(--contentColor, #fff)',
    boxShadow: '0 8px 24px rgba(12, 41, 84, 0.055)',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  accessStripInactive: {
    borderColor: '#f5d0b5',
    backgroundColor: '#fff8f1',
  },
  accessItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
  },
  accessIconWell: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: 'rgba(61, 125, 221, 0.12)',
    color: theme.palette.primary.main,
  },
  accessIconWellInactive: {
    backgroundColor: 'rgba(236, 122, 18, 0.16)',
    color: '#EC7A12',
  },
  accessIcon: {
    fontSize: 18,
  },
  accessCopy: {
    minWidth: 0,
  },
  accessLabel: {
    fontSize: 11,
    color: '#647184',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    lineHeight: 1.3,
  },
  accessValue: {
    fontSize: 15,
    fontWeight: 700,
    color: '#263548',
    marginTop: 2,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  accessValueDays: {
    fontSize: 18,
    letterSpacing: '-0.02em',
  },
  accessNote: {
    gridColumn: '1 / -1',
    margin: theme.spacing(0.25, 1.5, 1),
    fontSize: 12,
    color: '#9A3412',
    fontWeight: 600,
  },
}));

const hasAccessInfo = (access) => {
  if (!access || typeof access !== 'object') {
    return false;
  }

  return (
    !!access.plan
    || (access.access_start_date != null && access.access_start_date !== '')
    || (access.access_end_date != null && access.access_end_date !== '')
    || (access.access_expires_on != null && access.access_expires_on !== '')
    || access.access_days_remaining != null
    || !!access.access_state
    || visibleSeatFields(access).length > 0
  );
};

const CLOSED_ACCESS_NOTES = {
  scheduled: 'Learning Hub access has not started',
  expired: 'Learning Hub access has expired',
};

const isLearningHubAccessActive = (access) => {
  if (!access) {
    return false;
  }

  if (access.access_active === false) {
    return false;
  }

  const state = String(access.access_state || '').toLowerCase();
  if (state && state !== 'active') {
    return false;
  }

  return true;
};

const formatAccessDate = (value) => {
  if (!value) {
    return '';
  }

  const parsed = moment(value, 'YYYY-MM-DD', true);
  const date = parsed.isValid() ? parsed : moment(value);
  return date.isValid() ? date.format('D MMM YYYY') : String(value);
};

const accessKindLabel = (plan) => (
  plan === 'freemium' ? 'Freemium trial' : 'Learning Hub licence'
);

const LearningHubAccessStrip = ({ access, className = '' }) => {
  const classes = useStyles();

  if (!hasAccessInfo(access)) {
    return null;
  }

  const active = isLearningHubAccessActive(access);
  const wellClass = `${classes.accessIconWell}${active ? '' : ` ${classes.accessIconWellInactive}`}`;
  const startDate = formatAccessDate(access.access_start_date);
  const expiresOn = formatAccessDate(access.access_end_date || access.access_expires_on);
  const daysRemaining = active && access.access_days_remaining != null
    ? String(access.access_days_remaining)
    : '';
  const packageName = planLabel(access.plan);
  const seats = visibleSeatFields(access);
  const inactiveNote = CLOSED_ACCESS_NOTES[access.access_state]
    || 'Your Learning Hub access is not currently active.';
  const hasVisibleItem = Boolean(
    packageName || startDate || expiresOn || daysRemaining || seats.length
  );
  if (active && !hasVisibleItem) {
    return null;
  }
  const stripClass = `${classes.accessStrip}${active ? '' : ` ${classes.accessStripInactive}`}${className ? ` ${className}` : ''}`;

  return (
    <div className={stripClass}>
      {packageName ? (
        <div className={classes.accessItem}>
          <span className={wellClass}><LocalOfferIcon className={classes.accessIcon} /></span>
          <div className={classes.accessCopy}>
            <div className={classes.accessLabel}>Your package</div>
            <div className={classes.accessValue}>{packageName}</div>
          </div>
        </div>
      ) : null}
      {packageName ? (
        <div className={classes.accessItem}>
          <span className={wellClass}><PersonIcon className={classes.accessIcon} /></span>
          <div className={classes.accessCopy}>
            <div className={classes.accessLabel}>Your access</div>
            <div className={classes.accessValue}>{accessKindLabel(access.plan)}</div>
          </div>
        </div>
      ) : null}
      {startDate ? (
        <div className={classes.accessItem}>
          <span className={wellClass}><EventAvailableIcon className={classes.accessIcon} /></span>
          <div className={classes.accessCopy}>
            <div className={classes.accessLabel}>Access started</div>
            <div className={classes.accessValue}>{startDate}</div>
          </div>
        </div>
      ) : null}
      {expiresOn ? (
        <div className={classes.accessItem}>
          <span className={wellClass}><EventIcon className={classes.accessIcon} /></span>
          <div className={classes.accessCopy}>
            <div className={classes.accessLabel}>Expires on</div>
            <div className={classes.accessValue}>{expiresOn}</div>
          </div>
        </div>
      ) : null}
      {daysRemaining ? (
        <div className={classes.accessItem}>
          <span className={wellClass}><QueryBuilderIcon className={classes.accessIcon} /></span>
          <div className={classes.accessCopy}>
            <div className={classes.accessLabel}>Days remaining</div>
            <div className={`${classes.accessValue} ${classes.accessValueDays}`}>{daysRemaining}</div>
          </div>
        </div>
      ) : null}
      {seats.map((seat) => (
        <div className={classes.accessItem} key={seat.key}>
          <span className={wellClass}><EventSeatIcon className={classes.accessIcon} /></span>
          <div className={classes.accessCopy}>
            <div className={classes.accessLabel}>{seat.label}</div>
            <div className={classes.accessValue}>{String(seat.value)}</div>
          </div>
        </div>
      ))}
      {!active ? (
        <Typography className={classes.accessNote} variant="caption">
          {inactiveNote}
        </Typography>
      ) : null}
    </div>
  );
};

export default LearningHubAccessStrip;
